import type { CareerSave, CompletedTournamentResult, EventResult, EventTier } from './types';
import type { SeasonEvent } from './types';
export const BASE_POINTS = [500, 300, 190, 135, 110, 95, 85, 75, 68, 60];
export function pointsForPosition(position: number, multiplier: number, tiedPlayers = 1) {
  const base = BASE_POINTS[position - 1] ?? Math.max(5, 55 - position);
  return Math.round((base * multiplier) / tiedPlayers);
}

/** Applies a completed tournament exactly once to persistent career records. */
export function recordTournamentResult(
  career: CareerSave,
  event: SeasonEvent,
  position: number,
  score: number,
  completedAt = new Date().toISOString(),
): CareerSave {
  const points = pointsForPosition(position, event.pointsMultiplier);
  return applyCompletedTournamentResult(career, event, {
    eventId: event.id,
    courseId: event.courseId,
    season: career.currentSeasonNumber,
    playerPosition: position,
    playerTotalScore: score,
    playerRoundScores: [],
    roundsCompleted: 0,
    cutResult: event.cutRule.enabled ? 'made' : 'no-cut',
    pointsAwarded: points,
    championId: '',
    championName: position === 1 ? career.playerProfile.name : '',
    championScore: score,
    completedAt,
  });
}

/** Full result application, including cut-aware and round-derived statistics. */
export function applyCompletedTournamentResult(
  career: CareerSave,
  event: SeasonEvent,
  completed: CompletedTournamentResult,
): CareerSave {
  const marker = `result:${completed.season}:${completed.eventId}`;
  if (career.narrativeFlags.includes(marker)) return career;
  const result: EventResult = {
    eventId: completed.eventId,
    season: completed.season,
    position: completed.playerPosition,
    score: completed.playerTotalScore,
    points: completed.pointsAwarded,
    tier: event.tier,
    completedAt: completed.completedAt,
    courseId: completed.courseId,
    champion: completed.championName,
    runnerUp: completed.runnerUpName,
    cutResult: completed.cutResult,
    bestRound: completed.bestPlayerRound,
  };
  const s = career.careerStatistics,
    major = event.tier === 'major';
  const made = completed.cutResult === 'made',
    missed = completed.cutResult === 'missed';
  const roundsBefore = s.roundsPlayed;
  const strokesBefore = s.scoringAverage * roundsBefore;
  const rounds = completed.playerRoundScores;
  const won = completed.playerPosition === 1,
    championship = event.tier === 'seasonChampionship';
  const lastEvent = event.scheduleIndex === 19;
  const historicalEvent = career.worldHistory.eventHistory[event.id];
  const historicalId = `${event.id}:${completed.season}`;
  if (!historicalEvent.pastChampions.some((result) => result.id === historicalId)) {
    historicalEvent.pastChampions.unshift({
      id: historicalId,
      season: completed.season,
      championId: completed.championId,
      runnerUpId: completed.runnerUpId ?? '',
      winningScore: completed.championScore,
      margin: 0,
    });
    historicalEvent.defendingChampionId = completed.championId;
    const championCareer = career.worldHistory.golferCareerHistory[completed.championId];
    if (championCareer) championCareer.careerWins++;
  }
  return {
    ...career,
    revision: career.revision,
    activeTournamentState: null,
    currentEventIndex: lastEvent ? 20 : career.currentEventIndex + 1,
    seasonStatus: lastEvent ? 'completed' : 'inProgress',
    seasonState: {
      ...career.seasonState,
      points: career.seasonState.points + completed.pointsAwarded,
      results: [...career.seasonState.results, result],
    },
    eventHistory: [...career.eventHistory, result],
    careerStatistics: {
      ...s,
      eventsPlayed: s.eventsPlayed + 1,
      tournamentsCompleted: s.tournamentsCompleted + 1,
      roundsPlayed: roundsBefore + completed.roundsCompleted,
      cutsMade: s.cutsMade + +made,
      cutsMissed: s.cutsMissed + +missed,
      wins: s.wins + +won,
      runnerUpFinishes: s.runnerUpFinishes + +(completed.playerPosition === 2),
      top5s: s.top5s + +(completed.playerPosition <= 5 && !missed),
      top10s: s.top10s + +(completed.playerPosition <= 10 && !missed),
      majorStarts: s.majorStarts + +major,
      majorCutsMade: s.majorCutsMade + +(major && made),
      majorTop10s: s.majorTop10s + +(major && !missed && completed.playerPosition <= 10),
      majorWins: s.majorWins + +(major && won),
      bestMajorFinish:
        major && !missed
          ? Math.min(s.bestMajorFinish ?? completed.playerPosition, completed.playerPosition)
          : s.bestMajorFinish,
      seasonChampionshipQualifications: s.seasonChampionshipQualifications + +championship,
      seasonChampionshipStarts: s.seasonChampionshipStarts + +championship,
      seasonChampionshipWins: s.seasonChampionshipWins + +(championship && won),
      bestSeasonChampionshipFinish: championship
        ? Math.min(
            s.bestSeasonChampionshipFinish ?? completed.playerPosition,
            completed.playerPosition,
          )
        : s.bestSeasonChampionshipFinish,
      scoringAverage: rounds.length
        ? (strokesBefore + rounds.reduce((a, b) => a + b, 0)) / (roundsBefore + rounds.length)
        : s.scoringAverage,
      bestRound: rounds.length ? Math.min(s.bestRound ?? Infinity, ...rounds) : s.bestRound,
      worstRound: rounds.length ? Math.max(s.worstRound ?? -Infinity, ...rounds) : s.worstRound,
    },
    qualifications: won
      ? [...new Set([...career.qualifications, 'current-season-winner'])]
      : career.qualifications,
    narrativeFlags: [
      ...career.narrativeFlags,
      marker,
      ...(major && won && !career.narrativeFlags.includes('firstMajorVictory')
        ? ['firstMajorVictory']
        : []),
    ],
    updatedAt: completed.completedAt,
  };
}
export class CareerRankingEngine {
  calculate(results: EventResult[], current: number) {
    const score = results
      .slice(-20)
      .reduce(
        (sum, r, i) =>
          sum +
          (21 - i) *
            (r.position === 1 ? 8 : r.position <= 10 ? 4 : 1) *
            (
              { standard: 1, signature: 1.25, major: 1.6, seasonChampionship: 1.7 } as Record<
                EventTier,
                number
              >
            )[r.tier],
        0,
      );
    return Math.max(
      1,
      Math.min(150, Math.round(current * 0.75 + Math.max(1, 75 - score / 50) * 0.25)),
    );
  }
}
export function startNextSeason(c: CareerSave): CareerSave {
  return {
    ...c,
    currentSeasonNumber: c.currentSeasonNumber + 1,
    currentEventIndex: 0,
    seasonStatus: 'inProgress',
    seasonState: { points: 0, standingsRank: 49, form: 0, results: [] },
    activeTournamentState: null,
    careerStatistics: {
      ...c.careerStatistics,
      seasonsPlayed: c.careerStatistics.seasonsPlayed + 1,
    },
    narrativeFlags: c.narrativeFlags.filter((f) => !f.startsWith('seasonal:')),
    updatedAt: new Date().toISOString(),
  };
}
